/**
 * PatientStickerDialog — patient identity sticker (preview + browser print).
 *
 * Ports Flutter `patient_list_screen.dart` sticker (`widgetToPrint`, :1000):
 * MRN · Name · DOB · Age · Gender · Nationality with the org logo top-right.
 *
 * Print: normal browser print for now (clone the sticker into a hidden iframe
 * and `window.print()` it — avoids the base-ui Dialog transform that blanks a
 * plain `@media print` region). NOTE: fixed-size label-printer output
 * (Flutter generates a 75×25mm PDF via `printPatientLabel`) is deferred — this
 * is a standard print of the sticker for now.
 *
 *   DEV TESTING block (`currentENV=="DEV" && isBeta`, :1144) — pdf width/
 *   height + orientation switch + Dynamic/Fixed-size print (print the sticker
 *   at a custom `@page` size). Gated via `enableDevTools` (parent passes
 *   DEV-env && isBeta). Flutter's "Print ZPL" is commented-out/no-op and has
 *   no browser bridge, so it is NOT ported.
 *
 * Data: MRN / Name / DOB / Age / Gender / Nationality off the PatientDatum
 * row; org logo = `brandingUrl` from the org-info endpoint (caller passes it).
 */

import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Printer } from 'lucide-react';
import { toast } from 'sonner';
import { patientAge } from '@/lib/lab-utils';
import type { PatientDatum } from '@/types/patient-api';

export interface PatientStickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: PatientDatum;
  /** Org branding logo — caller sources it from the org-info endpoint
   *  (`useOrganisationInfo`); null hides the logo slot. */
  brandingUrl?: string | null;
  /** Show the DEV-env+beta-only print-dimension testing block. Parent passes
   *  `isDevEnv && !!user.isBeta` (Flutter `currentENV=="DEV" && isBeta`). */
  enableDevTools?: boolean;
}

function fullName(p: PatientDatum): string {
  return (
    p.name ||
    [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ') ||
    '—'
  );
}

function formatDob(p: PatientDatum): string {
  if (p.dob) return p.dob;
  if (!p.birthDate) return '—';
  const d = new Date(p.birthDate);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function ageDisplay(p: PatientDatum): string {
  if (p.birthDate) {
    const d = new Date(p.birthDate);
    if (!Number.isNaN(d.getTime())) return String(patientAge(p.birthDate));
  }
  return p.age ?? '—';
}

/** Label + value, both bold (mirrors Flutter `richTextWidget`). */
function Field({ label, value }: { label: string; value: string }) {
  return (
    <p className="psl-line">
      <span>{label} </span>
      {value || '—'}
    </p>
  );
}

/** Sticker preview styling — Flutter `widgetToPrint` (600px, 14pt, logo h50). */
const PREVIEW_CSS = `
.psl-preview { width:600px; max-width:100%; padding:20px; background:#fff; color:#000; font-family:'Inter',sans-serif; box-sizing:border-box; display:flex; flex-direction:column; }
.psl-preview .psl-row1 { display:flex; align-items:flex-start; }
.psl-preview .psl-left { flex:2; min-width:0; display:flex; flex-direction:column; gap:8px; }
.psl-preview .psl-logo { flex:1; display:flex; justify-content:flex-end; padding-left:5px; }
.psl-preview .psl-logo img { height:50px; object-fit:contain; }
.psl-preview .psl-dobage { display:flex; align-items:flex-start; gap:8px; }
.psl-preview .psl-dob { width:150px; flex-shrink:0; }
.psl-preview .psl-row2 { display:flex; align-items:flex-start; margin-top:8px; }
.psl-preview .psl-gender { width:150px; flex-shrink:0; }
.psl-preview .psl-line { margin:0; font-size:14px; font-weight:700; line-height:1.2; }
`;

/**
 * Print a node via a hidden same-origin iframe. The sticker lives inside a
 * `position:fixed` + translate Dialog popup, so a plain `@media print`
 * region renders off-page (blank). Cloning into an isolated iframe prints
 * reliably. `pageSize` omitted → normal default page; provided (e.g.
 * `'200pt 40pt'`) → that `@page` size (dev testing).
 */
function printNode(node: HTMLElement | null, pageSize?: string) {
  if (!node) return;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(
    '<!doctype html><html><head><meta charset="utf-8"><style>' +
      (pageSize ? `@page{size:${pageSize};margin:0}` : '') +
      'html,body{margin:0;padding:0;background:#fff}' +
      PREVIEW_CSS +
      '</style></head><body>' +
      node.outerHTML +
      '</body></html>',
  );
  doc.close();

  let fired = false;
  const fire = () => {
    if (fired) return;
    fired = true;
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      setTimeout(() => iframe.remove(), 500);
    }
  };
  const imgs = Array.from(doc.images ?? []);
  if (imgs.length === 0) {
    setTimeout(fire, 60);
  } else {
    let pending = imgs.length;
    const tick = () => {
      if (--pending <= 0) fire();
    };
    imgs.forEach((im) => {
      if (im.complete) tick();
      else {
        im.onload = tick;
        im.onerror = tick;
      }
    });
    setTimeout(fire, 1500);
  }
}

export function PatientStickerDialog({
  open,
  onOpenChange,
  patient,
  brandingUrl,
  enableDevTools = false,
}: PatientStickerDialogProps) {
  const previewRef = useRef<HTMLDivElement>(null);

  // Dev-testing state — Flutter defaults pdfWidth='200', pdfHeight='40' (pt),
  // orientationLand=true (`patient_list_screen.dart:991-992`).
  const [pdfWidth, setPdfWidth] = useState('200');
  const [pdfHeight, setPdfHeight] = useState('40');
  const [landscape, setLandscape] = useState(true);

  const numeric = (v: string) => v.replace(/[^0-9.]/g, '');

  const mrn = patient.mrn ?? '';
  const name = fullName(patient);
  const dob = formatDob(patient);
  const age = ageDisplay(patient);
  const gender = patient.gender?.name ?? '';
  const nationality = patient.nationality?.code ?? '—';

  // Normal print — default page size.
  const handlePrint = () => printNode(previewRef.current);

  // Dev: print at a custom page size (Flutter "Dynamic Size Print").
  const handleDynamicPrint = () => {
    const w = parseFloat(pdfWidth) || 0;
    const h = parseFloat(pdfHeight) || 0;
    if (!w || !h) {
      toast.error('Enter a valid PDF width and height');
      return;
    }
    const [a, b] = landscape
      ? [Math.max(w, h), Math.min(w, h)]
      : [Math.min(w, h), Math.max(w, h)];
    printNode(previewRef.current, `${a}pt ${b}pt`);
  };

  // Dev: Flutter "Fixed Size Print" → PdfPageFormat(200, 40) landscape.
  const handleFixedPrint = () => printNode(previewRef.current, '200pt 40pt');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <style>{PREVIEW_CSS}</style>
        <DialogHeader>
          <DialogTitle>Patient Sticker</DialogTitle>
        </DialogHeader>

        <div className="flex justify-center py-2">
          <div
            ref={previewRef}
            className="psl-preview rounded-sm ring-1 ring-border-default"
          >
            <div className="psl-row1">
              <div className="psl-left">
                <Field label="MRN:" value={mrn} />
                <Field label="Name:" value={name} />
                <div className="psl-dobage">
                  <div className="psl-dob">
                    <Field label="DOB:" value={dob} />
                  </div>
                  <Field label="Age:" value={age} />
                </div>
              </div>
              {brandingUrl ? (
                <div className="psl-logo">
                  <img src={brandingUrl} alt="Practice logo" />
                </div>
              ) : null}
            </div>
            <div className="psl-row2">
              <div className="psl-gender">
                <Field label="Gender:" value={gender} />
              </div>
              <Field label="Nationality:" value={nationality} />
            </div>
          </div>
        </div>

        {/* DEV-env + beta only — print-dimension testing scaffold. */}
        {enableDevTools ? (
          <div className="mt-2 border-t border-border-default pt-3 space-y-3">
            <p className="text-sm font-bold text-status-error">
              ** DEV TESTING
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-xs font-medium text-text-muted">
                  PDF width
                </span>
                <Input
                  inputMode="decimal"
                  value={pdfWidth}
                  onChange={(e) => setPdfWidth(numeric(e.target.value))}
                  placeholder="Enter Pdf Width"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-text-muted">
                  PDF height
                </span>
                <Input
                  inputMode="decimal"
                  value={pdfHeight}
                  onChange={(e) => setPdfHeight(numeric(e.target.value))}
                  placeholder="Enter Pdf Height"
                />
              </label>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-text-primary">
                Is orientation Landscape
              </span>
              <Switch
                checked={landscape}
                onCheckedChange={setLandscape}
                aria-label="Toggle landscape orientation"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleDynamicPrint}>
                Dynamic Size Print
              </Button>
              <Button variant="outline" onClick={handleFixedPrint}>
                Fixed Size Print
              </Button>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
